import React, { useState } from 'react';
import {
  Box,
  Text,
  Stack,
  Progress,
  Divider,
  Flex,
  Icon,
  Button,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  IconButton,
  Textarea,
} from '@chakra-ui/react';
import { StarIcon, CheckIcon } from '@chakra-ui/icons';

const Rating = ({ value, onChange }) => {
  const handleClick = (rating) => {
    onChange(rating);
  };

  return (
    <Box>
      {[1, 2, 3, 4, 5].map((rating) => (
        <IconButton
          key={rating}
          icon={<StarIcon boxSize={5} color={rating <= value ? 'yellow.500' : 'gray.300'} />}
          aria-label={`Rate ${rating}`}
          onClick={() => handleClick(rating)}
        />
      ))}
    </Box>
  );
};

const Timeline = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');

  const orders = [
    {
      id: 1,
      orderDate: '2023-05-01',
      orderType: 'Statement of Purpose',
      writerAssigned: 'John Doe',
      estimatedDeliveryDate: '2023-05-10',
      progress: 50,
      delivered: false,
    },
    {
      id: 2,
      orderDate: '2023-05-05',
      orderType: 'Statement of Purpose',
      writerAssigned: 'Jane Smith',
      estimatedDeliveryDate: '2023-05-15',
      progress: 80,
      delivered: true,
      deliveredDate: '2023-05-14',
      rating: 4.5,
    },
    // Add more orders here...
  ];

  const openRatingModal = (orderId) => {
    const order = orders.find((order) => order.id === orderId);
    if (order) {
      setSelectedOrder(order);
      setIsRatingModalOpen(true);
      setRating(0);
      setFeedback('');
    }
  };

  const closeRatingModal = () => {
    setSelectedOrder(null);
    setIsRatingModalOpen(false);
    setRating(0);
    setFeedback('');
  };

  const handleRatingChange = (value) => {
    setRating(value);
  };

  const handleFeedbackChange = (event) => {
    setFeedback(event.target.value);
  };

  const submitRating = () => {
    // Find the order in the orders array
    const orderToUpdate = orders.find((order) => order.id === selectedOrder.id);

    if (orderToUpdate) {
      // Update the rating and feedback properties
      orderToUpdate.rating = rating;
      orderToUpdate.feedback = feedback;

      // Close the modal
      closeRatingModal();
    }
  };

  return (
    <Box p={4}>
      {orders.map((order) => (
        <Box key={order.id} bg="white" borderRadius="md" boxShadow="md" p={4} mb={4}>
          <Stack direction={['column', 'row']} alignItems="center" justify="space-between" mb={2}>
            <Text fontWeight="bold">Order #{order.id}</Text>
            <Text>{order.orderDate}</Text>
          </Stack>
          <Stack direction="column" spacing={2}>
            <Flex justify="space-between">
              <Text fontWeight="bold">Order Type:</Text>
              <Text>{order.orderType}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text fontWeight="bold">Writer Assigned:</Text>
              <Text>{order.writerAssigned}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text fontWeight="bold">Estimated Delivery:</Text>
              <Text>{order.estimatedDeliveryDate}</Text>
            </Flex>
            <Divider />
            <Flex justify="space-between">
              <Text fontWeight="bold">Progress:</Text>
              {order.delivered ? (
                <Tooltip label="Delivered">
                  <Box as={CheckIcon} color="green.500" boxSize={4} />
                </Tooltip>
              ) : (
                <Text>{order.progress}%</Text>
              )}
            </Flex>
            {order.delivered ? (
              <Flex justify="space-between" alignItems="center">
                <Text fontWeight="bold">Delivered Date:</Text>
                <Text>{order.deliveredDate}</Text>
                <Flex alignItems="center">
                  <Text fontWeight="bold">Rating:</Text>
                  <Tooltip label={`Rating: ${order.rating}`}>
                    <Box ml={2}>
                      <Rating value={order.rating} onChange={() => {}} />
                    </Box>
                  </Tooltip>
                </Flex>
              </Flex>
            ) : (
              <Button colorScheme="blue" mt={2}>
                Mark as Delivered
              </Button>
            )}
            {order.delivered && (
              <Button colorScheme="blue" mt={2} onClick={() => openRatingModal(order.id)}>
                Rate Order
              </Button>
            )}
          </Stack>
        </Box>
      ))}
      <Modal isOpen={isRatingModalOpen} onClose={closeRatingModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Rate Order #{selectedOrder?.id}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Rating:</FormLabel>
              <Rating value={rating} onChange={handleRatingChange} />
            </FormControl>
            <FormControl>
              <FormLabel>Feedback:</FormLabel>
              <Textarea value={feedback} onChange={handleFeedbackChange} rows={4} />
            </FormControl>
            <Button colorScheme="blue" mt={4} onClick={submitRating}>
              Submit Rating
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Timeline;
